// Shared helpers for converting between the 24-hour display strings
// TimePicker produces (e.g. "09:00") and the 24-hour "HH:MM:SS" strings
// the backend's `time` fields expect. Used by both the Organization Settings
// predefined-shift modal and the job form's custom shift fields, so the two
// never drift on how a time round-trips.

const TIME_24H_REGEX = /^(\d{1,2}):(\d{2})$/;

/** "09:00" -> 540 (minutes since midnight). Returns null if unparsable. */
export function parseTimeToMinutes(value) {
  if (!value || typeof value !== "string") return null;
  const match = value.trim().match(TIME_24H_REGEX);
  if (!match) return null;
  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (Number.isNaN(hour) || Number.isNaN(minute) || hour > 23 || minute > 59) return null;
  return hour * 60 + minute;
}

/** "09:00" -> "09:00:00". Returns null if unparsable. */
export function to24HourTime(value) {
  const minutes = parseTimeToMinutes(value);
  if (minutes === null) return null;
  const hh = String(Math.floor(minutes / 60)).padStart(2, "0");
  const mm = String(minutes % 60).padStart(2, "0");
  return `${hh}:${mm}:00`;
}

/** "09:00:00" or "09:00" -> "09:00". Returns "" if falsy/unparsable. */
export function to24HourDisplay(value) {
  if (!value || typeof value !== "string") return "";
  const [hStr, mStr] = value.split(":");
  const h = parseInt(hStr, 10);
  const m = (mStr || "00").padStart(2, "0");
  if (Number.isNaN(h)) return "";
  return `${String(h).padStart(2, "0")}:${m}`;
}

/**
 * Reads a free-text shift range — "09:30 - 18:30", "9:30 - 18:30 UTC" —
 * into `{ startTime, endTime }` as 24-hour display strings. Returns nulls
 * when the text carries no recognisable range.
 *
 * Also reads a legacy AM/PM range ("9:30 AM - 6:30 PM UTC") correctly,
 * rather than just grabbing the leading digits and silently dropping PM.
 * The one caller besides the AI JD parser is CloneJobForm, reading a source
 * job's old free-text `shift_timing` — jobs created before this format
 * changed still hold text written in the old convention, and misreading
 * "6:30 PM" as 06:30 would be a real data bug, not a display nit.
 *
 * Only the times come back. Any trailing zone in the text is an abbreviation
 * ("IST", "CST"), and those collide across regions — TimezoneSelect stores
 * IANA names for exactly that reason, so guessing one here would write the
 * wrong zone. The caller asks the user to pick it.
 *
 * The two callers are the AI JD parser, which returns the shift as one free
 * -text line (see pages/admin/job/AIModal), and CloneJobForm reading a
 * legacy job's stored text. Everything else in the app already holds start
 * and end apart.
 */
export function parseShiftRange(value) {
  const empty = { startTime: null, endTime: null };
  if (!value || typeof value !== "string") return empty;

  // Drop a trailing zone abbreviation so it can't be read as part of a time —
  // but not AM/PM, which is the same shape as a zone name and may still be
  // legacy content.
  const timePart = value.trim().replace(/\s+(?!AM\b|PM\b)[A-Z]{2,5}$/i, "");
  const parts = timePart.split(/\s*[-–—]\s*/);
  if (parts.length !== 2) return empty;

  const extractTime = (s) => {
    const text = s.trim();

    const withMinutes12h = text.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (withMinutes12h) {
      let hour = parseInt(withMinutes12h[1], 10) % 12;
      if (withMinutes12h[3].toUpperCase() === "PM") hour += 12;
      return `${String(hour).padStart(2, "0")}:${withMinutes12h[2]}`;
    }
    const wholeHour12h = text.match(/(\d{1,2})\s*(AM|PM)/i);
    if (wholeHour12h) {
      let hour = parseInt(wholeHour12h[1], 10) % 12;
      if (wholeHour12h[2].toUpperCase() === "PM") hour += 12;
      return `${String(hour).padStart(2, "0")}:00`;
    }

    const match = text.match(/(\d{1,2}):(\d{2})/);
    if (!match) return null;
    const hour = parseInt(match[1], 10);
    if (Number.isNaN(hour) || hour > 23) return null;
    return `${String(hour).padStart(2, "0")}:${match[2]}`;
  };

  const startTime = extractTime(parts[0]);
  const endTime = extractTime(parts[1]);
  if (!startTime || !endTime) return empty;
  return { startTime, endTime };
}

/** (9, "30", "AM") -> "09:30" */
function to24HourNative(h12, mm, period) {
  let h = h12 % 12;
  if (period.toUpperCase() === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${mm}`;
}
